import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Lock, Unlock, Archive, ArchiveRestore } from 'lucide-react';
import { api } from '../context/AuthContext';

export const CaseNumbers = ({ caseDetail, isTeam, onUpdated }) => {
  const [editAz, setEditAz] = useState(false);
  const [editMnr, setEditMnr] = useState(false);
  const [az, setAz] = useState(caseDetail.aktenzeichen || '');
  const [mnr, setMnr] = useState(caseDetail.mandanten_nr || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset local state when caseDetail switches to a different case
  useEffect(() => {
    setAz(caseDetail.aktenzeichen || '');
    setMnr(caseDetail.mandanten_nr || '');
    setEditAz(false);
    setEditMnr(false);
    setError('');
  }, [caseDetail.id, caseDetail.aktenzeichen, caseDetail.mandanten_nr]);

  const saveAz = async () => {
    if (!az.trim() || az === caseDetail.aktenzeichen) { setEditAz(false); return; }
    setSaving(true); setError('');
    try {
      const r = await api.patch(`/cases/${caseDetail.id}`, { aktenzeichen: az.trim() });
      onUpdated?.(r.data);
      setEditAz(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const saveMnr = async () => {
    if (!mnr.trim() || mnr === caseDetail.mandanten_nr) { setEditMnr(false); return; }
    setSaving(true); setError('');
    try {
      if (caseDetail.mandanten_nr) await api.patch(`/cases/mandanten/${caseDetail.mandanten_nr}`, { mandanten_nr: mnr.trim() });
      else await api.patch(`/cases/${caseDetail.id}`, { mandanten_nr: mnr.trim() });
      onUpdated?.({ mandanten_nr: mnr.trim(), chat_enabled: caseDetail.chat_enabled ?? true });
      setEditMnr(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const vergeben = () => {
    setError('');
    setMnr('');
    setEditMnr(true);
  };

  const toggleChat = async () => {
    if (!caseDetail.mandanten_nr || saving) return;
    const next = caseDetail.chat_enabled === false;
    // Optimistic update so user sees the change immediately
    onUpdated?.({ chat_enabled: next });
    setSaving(true); setError('');
    try {
      const r = await api.patch(`/cases/mandanten/${caseDetail.mandanten_nr}`, { chat_enabled: next });
      // Server bestätigt — finalen Wert übernehmen
      const final = r.data?.chat_enabled ?? next;
      onUpdated?.({ chat_enabled: final });
    } catch (e) {
      // Rollback bei Fehler
      onUpdated?.({ chat_enabled: !next });
      setError(e?.response?.data?.detail || 'Chat-Status konnte nicht geändert werden');
    } finally { setSaving(false); }
  };

  const toggleArchived = async () => {
    if (!caseDetail.id || saving) return;
    const next = !caseDetail.archived;
    onUpdated?.({ archived: next });
    setSaving(true); setError('');
    try {
      const r = await api.patch(`/cases/${caseDetail.id}`, { archived: next });
      onUpdated?.({ archived: r.data?.archived ?? next });
    } catch (e) {
      onUpdated?.({ archived: !next });
      setError(e?.response?.data?.detail || 'Archiv-Status konnte nicht geändert werden');
    } finally { setSaving(false); }
  };

  const changeStatus = async (newStatus) => {
    if (!caseDetail.id || saving) return;
    const prevStatus = caseDetail.status || 'neu';
    const prevArchived = !!caseDetail.archived;
    const done = newStatus === 'abgeschlossen' || newStatus === 'abgelehnt';
    onUpdated?.({ status: newStatus, archived: done });
    setSaving(true); setError('');
    try {
      const r = await api.patch(`/cases/${caseDetail.id}`, { status: newStatus });
      onUpdated?.({
        status: r.data?.status ?? newStatus,
        archived: r.data?.archived ?? done,
      });
    } catch (e) {
      onUpdated?.({ status: prevStatus, archived: prevArchived });
      setError(e?.response?.data?.detail || 'Status konnte nicht geändert werden');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="case-numbers">
      {/* Mandantennummer */}
      <div className="flex items-center gap-1.5 bg-[#f5f8fa] border border-gray-200 px-2.5 py-1">
        <span className="text-[10px] tracking-[0.2em] text-[#34536b]">M-NR</span>
        {editMnr ? (
          <>
            <input
              value={mnr}
              onChange={(e) => setMnr(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="aus DATEV"
              inputMode="numeric"
              title="Mandantennummer aus DATEV eintragen (4XXXX)"
              data-testid="edit-mandanten-nr"
              className="w-20 px-1.5 py-0.5 text-xs border border-[#b8893d] bg-white font-mono"
              autoFocus
            />
            <button onClick={saveMnr} disabled={saving} data-testid="save-mandanten-nr" className="text-[#b8893d] hover:text-[#a07730] text-xs">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => { setMnr(caseDetail.mandanten_nr || ''); setEditMnr(false); }} className="text-[#34536b] hover:text-rose-600 text-xs">×</button>
          </>
        ) : (
          <>
            <span className="text-xs font-mono text-[#1B3A5C]" data-testid="display-mandanten-nr">{caseDetail.mandanten_nr || '—'}</span>
            {isTeam && caseDetail.mandanten_nr && (
              <button onClick={() => setEditMnr(true)} data-testid="edit-mandanten-nr-btn" className="text-[#b8893d] hover:underline text-[10px] ml-1 tracking-wider">
                ÄNDERN
              </button>
            )}
            {isTeam && !caseDetail.mandanten_nr && (
              <button onClick={vergeben} data-testid="assign-mandanten-nr-btn" title="Nächste freie Nummer vorschlagen und vergeben"
                className="text-white bg-[#b8893d] hover:bg-[#a07730] text-[10px] ml-1 px-2 py-0.5 tracking-wider">
                VERGEBEN
              </button>
            )}
          </>
        )}
      </div>

      {/* Chat-Freischaltung (Team only) */}
      {isTeam && caseDetail.mandanten_nr && (
        <button
          type="button"
          onClick={toggleChat}
          disabled={saving}
          data-testid="toggle-chat-enabled"
          className={`flex items-center gap-1.5 px-2.5 py-1 border text-xs tracking-wider transition-colors disabled:opacity-60 ${
            caseDetail.chat_enabled !== false
              ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700'
              : 'bg-rose-50 border-rose-300 text-rose-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700'
          }`}
          title={caseDetail.chat_enabled !== false ? 'Chat für den Mandanten sperren' : 'Chat wieder freigeben'}
        >
          {saving ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> SPEICHERT…</>
          ) : caseDetail.chat_enabled !== false ? (
            <><Unlock className="w-3 h-3" /> CHAT OFFEN</>
          ) : (
            <><Lock className="w-3 h-3" /> CHAT GESPERRT</>
          )}
        </button>
      )}

      {/* Chat-Status (Mandanten-View, nur lesbar) */}
      {!isTeam && caseDetail.chat_enabled !== true && (
        <span className="flex items-center gap-1 px-2.5 py-1 bg-rose-50 border border-rose-300 text-rose-700 text-xs tracking-wider" data-testid="client-chat-locked-pill">
          <Lock className="w-3 h-3" /> CHAT GESPERRT
        </span>
      )}

      {/* Status-Klassifikation (Team only, nicht für Allgemein-Chats) */}
      {isTeam && !caseDetail.is_general && caseDetail.id && (
        <div
          className="flex items-center gap-1.5 bg-[#f5f8fa] border border-gray-200 px-2.5 py-1"
          data-testid="case-status-control"
        >
          <span className="text-[10px] tracking-[0.2em] text-[#34536b]">STATUS</span>
          <select
            value={caseDetail.status || 'neu'}
            disabled={saving}
            onChange={(e) => changeStatus(e.target.value)}
            data-testid="case-status-select"
            className="text-xs border border-[#1B3A5C]/30 bg-white text-[#1B3A5C] px-1.5 py-0.5 tracking-wider rounded-sm disabled:opacity-60"
            title="Bearbeitungsstand der Akte ändern"
          >
            <option value="neu">NEU</option>
            <option value="in_bearbeitung">IN BEARBEITUNG</option>
            <option value="kontaktiert">KONTAKTIERT</option>
            <option value="abgeschlossen">ABGESCHLOSSEN</option>
            <option value="abgelehnt">ABGELEHNT</option>
          </select>
        </div>
      )}

      {/* Archivieren (Team only, nicht für Allgemein-Chats) */}
      {isTeam && !caseDetail.is_general && caseDetail.id && (
        <button
          type="button"
          onClick={toggleArchived}
          disabled={saving}
          data-testid="toggle-archived"
          className={`flex items-center gap-1.5 px-2.5 py-1 border text-xs tracking-wider transition-colors disabled:opacity-60 ${
            caseDetail.archived
              ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700'
              : 'bg-white border-gray-200 text-[#34536b] hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700'
          }`}
          title={caseDetail.archived ? 'Akte aus Archiv holen' : 'Akte ins Archiv verschieben'}
        >
          {saving ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> …</>
          ) : caseDetail.archived ? (
            <><ArchiveRestore className="w-3 h-3" /> ARCHIVIERT</>
          ) : (
            <><Archive className="w-3 h-3" /> ARCHIVIEREN</>
          )}
        </button>
      )}

      {/* Aktenzeichen — only for real cases */}
      {!caseDetail.is_general && (
        <div className="flex items-center gap-1.5 bg-[#f5f8fa] border border-gray-200 px-2.5 py-1">
          <span className="text-[10px] tracking-[0.2em] text-[#34536b]">AKTENZ.</span>
          {editAz ? (
            <>
              <input
                value={az}
                onChange={(e) => setAz(e.target.value)}
                placeholder="AZ aus DATEV (000000-26)"
                data-testid="edit-aktenzeichen"
                className="w-32 px-1.5 py-0.5 text-xs border border-[#b8893d] bg-white font-mono"
                autoFocus
              />
              <button onClick={saveAz} disabled={saving} data-testid="save-aktenzeichen" className="text-[#b8893d] hover:text-[#a07730] text-xs">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => { setAz(caseDetail.aktenzeichen || ''); setEditAz(false); }} className="text-[#34536b] hover:text-rose-600 text-xs">×</button>
            </>
          ) : (
            <>
              <span className="text-xs font-mono text-[#1B3A5C]" data-testid="display-aktenzeichen">{caseDetail.aktenzeichen || '—'}</span>
              {isTeam && (
                <button onClick={() => setEditAz(true)} data-testid="edit-aktenzeichen-btn" className="text-[#b8893d] hover:underline text-[10px] ml-1 tracking-wider">
                  ÄNDERN
                </button>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <span className="text-xs text-rose-700 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </span>
      )}
    </div>
  );
};

export default CaseNumbers;
